import type { FC, PropsWithChildren } from "react";
import { useParams } from "react-router-dom";

import TrialFormIdentityContext from "src/contexts/data/TrialFormIdentityContext";
import useTrialFormIdentityById from "src/hooks/data/load/useTrialFormIdentityById";

/**
 * Loads trial form identity from the matching route parameter for descendants.
 *
 * Preconditions: Render within the router that supplies the route parameters.
 * Postconditions: An absent or empty parameter becomes undefined; otherwise base-10 parseInt
 * supplies the ID, accepting numeric prefixes and producing NaN for unparseable text.
 * Loading and errors remain in context; this wrapper does not gate children or enforce access.
 * Assumptions: The loader handles request state and the server enforces authorization.
 */
export const RouteTrialFormIdentityProvider: FC<PropsWithChildren> = ({ children }) => {
    const { trialFormId } = useParams<{ trialFormId: string }>();
    const context = useTrialFormIdentityById(
        trialFormId ? Number.parseInt(trialFormId, 10) : undefined
    );

    return (
        <TrialFormIdentityContext.Provider value={context}>
            {children}
        </TrialFormIdentityContext.Provider>
    );
};
